/**
 * Busca, filtros, ordenação e paginação do Fairtrade (client-side).
 */

import {
  FAIRTRADE_API_CATEGORY_IDS,
  fairtradeCollectionFamilyKey,
  getFairtradeRowDisplayPrices,
  isFairtradeCollectionHub,
  type FairtradeCollectionPriceStats,
  type FairtradePowerRow,
  type FairtradeStatusFilter,
} from '@/lib/fairtrade-catalog';

export type FairtradeSortId =
  | 'name-asc'
  | 'name-desc'
  | 'category-asc'
  | 'category-desc'
  | 'status-limited'
  | 'status-unlimited'
  | 'xats-asc'
  | 'xats-desc'
  | 'days-asc'
  | 'days-desc'
  | 'store-asc'
  | 'store-desc'
  | 'id-desc'
  | 'id-asc';

export type FairtradeSortColumn =
  | 'power'
  | 'category'
  | 'status'
  | 'xats'
  | 'days'
  | 'store'
  | 'id';

export type FairtradePageSize = 25 | 50 | 100;

export type FairtradeQueryState = {
  query: string;
  type: string; // '' | type key
  status: FairtradeStatusFilter;
  minXats: number | null;
  maxXats: number | null;
  minDays: number | null;
  maxDays: number | null;
  sort: FairtradeSortId;
  page: number;
  pageSize: FairtradePageSize;
};

export const DEFAULT_FAIRTRADE_QUERY: FairtradeQueryState = {
  query: '',
  type: '',
  status: 'all',
  minXats: null,
  maxXats: null,
  minDays: null,
  maxDays: null,
  sort: 'id-desc',
  page: 1,
  pageSize: 25,
};

export const FAIRTRADE_SORT_OPTIONS: { id: FairtradeSortId; label: string }[] = [
  { id: 'id-desc', label: 'ID mais recente' },
  { id: 'id-asc', label: 'ID mais antigo' },
  { id: 'name-asc', label: 'Nome: A → Z' },
  { id: 'name-desc', label: 'Nome: Z → A' },
  { id: 'category-asc', label: 'Categoria: A → Z' },
  { id: 'category-desc', label: 'Categoria: Z → A' },
  { id: 'status-limited', label: 'Limitados primeiro' },
  { id: 'status-unlimited', label: 'Ilimitados primeiro' },
  { id: 'xats-asc', label: 'Menor preço (xats)' },
  { id: 'xats-desc', label: 'Maior preço (xats)' },
  { id: 'days-asc', label: 'Menor preço (days)' },
  { id: 'days-desc', label: 'Maior preço (days)' },
  { id: 'store-asc', label: 'Menor preço (loja)' },
  { id: 'store-desc', label: 'Maior preço (loja)' },
];

export const FAIRTRADE_PAGE_SIZES: FairtradePageSize[] = [25, 50, 100];

const SORT_COLUMN_PAIR: Record<FairtradeSortColumn, [FairtradeSortId, FairtradeSortId]> = {
  power: ['name-asc', 'name-desc'],
  category: ['category-asc', 'category-desc'],
  status: ['status-limited', 'status-unlimited'],
  xats: ['xats-asc', 'xats-desc'],
  days: ['days-asc', 'days-desc'],
  store: ['store-asc', 'store-desc'],
  id: ['id-desc', 'id-asc'],
};

export function toggleFairtradeColumnSort(
  column: FairtradeSortColumn,
  current: FairtradeSortId
): FairtradeSortId {
  const [primary, secondary] = SORT_COLUMN_PAIR[column];
  return current === primary ? secondary : primary;
}

export function fairtradeSortColumnOf(sort: FairtradeSortId): FairtradeSortColumn | null {
  for (const [column, pair] of Object.entries(SORT_COLUMN_PAIR) as Array<
    [FairtradeSortColumn, [FairtradeSortId, FairtradeSortId]]
  >) {
    if (pair.includes(sort)) return column;
  }
  return null;
}

export function fairtradeSortIsAsc(sort: FairtradeSortId): boolean {
  return sort.endsWith('-asc') || sort === 'status-unlimited';
}

function effectiveXats(
  row: FairtradePowerRow,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): number {
  const prices = getFairtradeRowDisplayPrices(row, collectionPrices);
  return prices.minXats || prices.maxXats || 0;
}

function effectiveDays(
  row: FairtradePowerRow,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): number {
  const prices = getFairtradeRowDisplayPrices(row, collectionPrices);
  return prices.minDays || prices.maxDays || 0;
}

function effectiveStore(row: FairtradePowerRow): number {
  const raw = String(row.storePrice ?? '').trim().toLowerCase();
  if (!raw || raw === '0' || raw.includes('free') || raw.includes('gratuito')) return 0;
  const n = Number(raw.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function matchesPowerId(row: FairtradePowerRow, q: string): boolean {
  const idStr = String(row.id);
  if (!idStr) return false;
  if (idStr === q || idStr.includes(q)) return true;
  // Aceita "id 750", "id:750", "id=750", "#750"
  const stripped = q.replace(/^(#|id)\s*[:=]?\s*/i, '').trim();
  if (stripped && stripped !== q) {
    return idStr === stripped || idStr.includes(stripped);
  }
  return false;
}

function matchesQuery(row: FairtradePowerRow, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  if (matchesPowerId(row, q)) return true;
  if (row.name.toLowerCase().includes(q)) return true;
  if (row.slug.toLowerCase().includes(q)) return true;
  if (row.title.toLowerCase().includes(q)) return true;
  if (row.type.toLowerCase().includes(q) || row.typeLabel.toLowerCase().includes(q)) {
    return true;
  }
  if (row.category.toLowerCase().includes(q)) return true;
  if (row.categories.some((c) => c.toLowerCase().includes(q))) return true;
  return false;
}

/** Filtro por card: categoria `/powers` ou família de coleção (ex.: superfarm). */
export function matchesFairtradeTypeFilter(
  row: FairtradePowerRow,
  filter: string
): boolean {
  const key = filter.trim().toLowerCase();
  if (!key) return true;
  if (FAIRTRADE_API_CATEGORY_IDS.has(key)) {
    return row.category === key;
  }
  const family = fairtradeCollectionFamilyKey(row);
  return family === key || row.type === key || row.name === key;
}

export function filterFairtradeRows(
  rows: FairtradePowerRow[],
  state: Pick<
    FairtradeQueryState,
    'query' | 'type' | 'status' | 'minXats' | 'maxXats' | 'minDays' | 'maxDays'
  >,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): FairtradePowerRow[] {
  return rows.filter((row) => {
    // Hubs (superbf, superanime, …) não entram na lista — só os powers da coleção.
    if (isFairtradeCollectionHub(row)) return false;
    if (!matchesQuery(row, state.query)) return false;
    if (state.type && !matchesFairtradeTypeFilter(row, state.type)) return false;
    if (state.status === 'limited' && !row.flags.limited) return false;
    if (state.status === 'unlimited' && row.flags.limited) return false;

    const x = effectiveXats(row, collectionPrices);
    const d = effectiveDays(row, collectionPrices);

    if (state.minXats != null && x < state.minXats) return false;
    if (state.maxXats != null && x > state.maxXats) return false;
    if (state.minDays != null && d < state.minDays) return false;
    if (state.maxDays != null && d > state.maxDays) return false;

    return true;
  });
}

export function sortFairtradeRows(
  rows: FairtradePowerRow[],
  sort: FairtradeSortId,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): FairtradePowerRow[] {
  const next = [...rows];
  const byName = (a: FairtradePowerRow, b: FairtradePowerRow) =>
    a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' });
  const byXats = (a: FairtradePowerRow, b: FairtradePowerRow) =>
    effectiveXats(a, collectionPrices) - effectiveXats(b, collectionPrices);
  const byDays = (a: FairtradePowerRow, b: FairtradePowerRow) =>
    effectiveDays(a, collectionPrices) - effectiveDays(b, collectionPrices);

  switch (sort) {
    case 'name-asc':
      next.sort(byName);
      break;
    case 'name-desc':
      next.sort((a, b) => byName(b, a));
      break;
    case 'category-asc':
      next.sort(
        (a, b) =>
          a.typeLabel.localeCompare(b.typeLabel, 'pt', { sensitivity: 'base' }) ||
          byName(a, b)
      );
      break;
    case 'category-desc':
      next.sort(
        (a, b) =>
          b.typeLabel.localeCompare(a.typeLabel, 'pt', { sensitivity: 'base' }) ||
          byName(a, b)
      );
      break;
    case 'status-limited':
      next.sort(
        (a, b) => Number(b.flags.limited) - Number(a.flags.limited) || byName(a, b)
      );
      break;
    case 'status-unlimited':
      next.sort(
        (a, b) => Number(a.flags.limited) - Number(b.flags.limited) || byName(a, b)
      );
      break;
    case 'xats-asc':
      next.sort((a, b) => byXats(a, b) || byName(a, b));
      break;
    case 'xats-desc':
      next.sort((a, b) => byXats(b, a) || byName(a, b));
      break;
    case 'days-asc':
      next.sort((a, b) => byDays(a, b) || byName(a, b));
      break;
    case 'days-desc':
      next.sort((a, b) => byDays(b, a) || byName(a, b));
      break;
    case 'store-asc':
      next.sort((a, b) => effectiveStore(a) - effectiveStore(b) || byName(a, b));
      break;
    case 'store-desc':
      next.sort((a, b) => effectiveStore(b) - effectiveStore(a) || byName(a, b));
      break;
    case 'id-desc':
      next.sort((a, b) => b.id - a.id || byName(a, b));
      break;
    case 'id-asc':
      next.sort((a, b) => a.id - b.id || byName(a, b));
      break;
    default:
      break;
  }
  return next;
}

export function filterAndSortFairtradeRows(
  rows: FairtradePowerRow[],
  state: FairtradeQueryState,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): FairtradePowerRow[] {
  const filtered = filterFairtradeRows(rows, state, collectionPrices);
  const sorted = sortFairtradeRows(filtered, state.sort, collectionPrices);
  // Só destaca o último lançamento real quando ele passa nos filtros e a ordenação é por ID recente.
  if (state.sort === 'id-desc') {
    return pinLatestFairtradePowerFirst(sorted);
  }
  return sorted;
}

/** Garante que o último power lançado (flag isLatest) fica em 1º — só se estiver na lista filtrada. */
export function pinLatestFairtradePowerFirst(
  rows: FairtradePowerRow[]
): FairtradePowerRow[] {
  if (rows.length < 2) return rows;

  const latestIdx = rows.findIndex((r) => r.flags.isLatest);
  if (latestIdx <= 0) return rows;

  const next = [...rows];
  const [latest] = next.splice(latestIdx, 1);
  next.unshift(latest);
  return next;
}

export function paginateFairtradeRows<T>(
  rows: T[],
  page: number,
  pageSize: FairtradePageSize
): {
  pageItems: T[];
  total: number;
  totalPages: number;
  safePage: number;
  rangeStart: number;
  rangeEnd: number;
} {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = rows.slice(start, start + pageSize);
  const rangeStart = total === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + pageSize, total);
  return { pageItems, total, totalPages, safePage, rangeStart, rangeEnd };
}

export function hasActiveFairtradeFilters(
  state: Pick<
    FairtradeQueryState,
    'query' | 'type' | 'status' | 'minXats' | 'maxXats' | 'minDays' | 'maxDays'
  >
): boolean {
  return Boolean(
    state.query.trim() ||
      state.type ||
      state.status !== 'all' ||
      state.minXats != null ||
      state.maxXats != null ||
      state.minDays != null ||
      state.maxDays != null
  );
}
